// hooks/useJournalTransfer.js
/* global chrome */
import { useContext, useState, useCallback } from 'react';
import { UserContext } from '../../../UserContext';
import { applyOrderFromTemplate } from '../utils/sectionHelpers';
import apiClient from '../../../apiClient';

/**
 * Custom hook för överföring av journal till externa system
 * Hanterar TakeCare, PMO, Kaddio, Clinicbuddy, WebDoc och SLS_PDF
 */
export const useJournalTransfer = (sections, selectedTemplate, sessionId) => {
  const { user } = useContext(UserContext);
  const selectedJournalSystem = user?.clinic?.journal_system || "Kaddio";

  // State för signeringsdialog
  const [showSignPopup, setShowSignPopup] = useState(false);
  const [hasCertificate, setHasCertificate] = useState(false);
  const [signError, setSignError] = useState(null);
  const [isSigningLoading, setIsSigningLoading] = useState(false);

  // Generera PDF (med eller utan signering)
  const generatePdf = useCallback(async (sign = false, password = '') => {
    const orderedSecs = applyOrderFromTemplate(sections, selectedTemplate);
    const gptRubriker = orderedSecs.map((sec) => ({
      title: sec.title,
      content: sec.content.trim(),
    }));

    const response = await apiClient.post('/api/generate-sls-pdf', {
      journal_sections: gptRubriker,
      template_name: selectedTemplate?.template_name || '',
      session_id: sessionId,
      sign: sign,
      certificate_password: password
    }, {
      responseType: 'blob'
    });

    // Kolla om svaret är ett fel (JSON istället för blob)
    const contentType = response.headers['content-type'];
    if (contentType && contentType.includes('application/json')) {
      // Läs blob som text och parsa JSON
      const text = await response.data.text();
      const errorData = JSON.parse(text);
      throw new Error(errorData.details || errorData.error || 'Okänt fel');
    }

    // Hämta PDF som blob
    const blob = response.data;
    const now = new Date();
    const timestamp = `${now.toISOString().split('T')[0]}_${now.toTimeString().slice(0,8).replace(/:/g, '')}`;
    const suffix = sign ? '_signerad' : '';
    const filename = `SLS_Journal_${timestamp}${suffix}.pdf`;

    // Spara PDF (Electron eller webb)
    if (window.aionDesktop && window.aionDesktop.savePdfAs) {
      // Electron: Använd native "Save As" dialog
      const result = await window.aionDesktop.savePdfAs(blob, filename);
      if (result.success) {
        alert(`Journal-PDF sparad till:\n${result.path}`);
      } else {
        alert('Kunde inte spara PDF');
      }
    } else {
      // Webb: Vanlig browser download
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      alert(sign ? 'Signerad journal-PDF nedladdad!' : 'Journal-PDF nedladdad!');
    }
  }, [sections, selectedTemplate, sessionId]);

  // Hantera signering
  const handleSignPdf = useCallback(async (password) => {
    setIsSigningLoading(true);
    setSignError(null);
    try {
      await generatePdf(true, password);
      setShowSignPopup(false);
    } catch (error) {
      console.error('PDF signing error:', error);
      setSignError(error.message || 'Kunde inte signera PDF');
    } finally {
      setIsSigningLoading(false);
    }
  }, [generatePdf]);

  // Hoppa över signering
  const handleSkipSign = useCallback(async () => {
    setShowSignPopup(false);
    setSignError(null);
    try {
      await generatePdf(false);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert(`Kunde inte generera PDF: ${error.message}`);
    }
  }, [generatePdf]);

  // Avbryt signering
  const handleCancelSign = useCallback(() => {
    setShowSignPopup(false);
    setSignError(null);
  }, []);

  const handleTransferClick = async () => {
    try {
      // Sortera enligt mallen varje gång man ska skicka
      const orderedSecs = applyOrderFromTemplate(sections, selectedTemplate);

      const gptRubriker = orderedSecs.map((sec) => ({
        title: sec.title,
        content: sec.content.trim(),
      }));

      // ========== SLS_PDF: Visa signeringsdialog ==========
      if (selectedJournalSystem === "SLS_PDF") {
        try {
          // Kolla om certifikat finns
          const certStatus = await apiClient.get('/api/certificate/status');
          setHasCertificate(certStatus.data.has_certificate);
          setSignError(null);
          setShowSignPopup(true);
          return;
        } catch (error) {
          console.error('Certificate status check error:', error);
          // Fortsätt ändå och visa popup utan certifikat
          setHasCertificate(false);
          setSignError(null);
          setShowSignPopup(true);
          return;
        }
      }

      // Kaddio & Clinicbuddy – skickas via Chrome-extension
      if (["Kaddio", "Clinicbuddy", "Webdoc"].includes(selectedJournalSystem)) {
        const message = {
          type: "TRANSFER_JOURNAL",
          targetSystem: selectedJournalSystem,
          journalData: gptRubriker,
        };

        chrome.runtime.sendMessage(
          "hikcpghkdgniminoebfhpjemgfmcfpoi",
          message,
          (response) => {
            if (chrome.runtime.lastError) {
              console.error(
                "Extension-fel:",
                chrome.runtime.lastError.message
              );
            } else {
              console.log("Svar från extension:", response);
            }
          }
        );
        return;
      }

      // Lokala desktop-verktygen (PMO & TakeCare)
      let ENDPOINT = null;
      if (selectedJournalSystem === "PMO") {
        ENDPOINT = "http://127.0.0.1:5011/send";
        window.aionDesktop?.startTransfer?.("PMO");
      } else if (selectedJournalSystem === "TakeCare") {
        ENDPOINT = "http://127.0.0.1:5010/send";
        window.aionDesktop?.startTransfer?.("TakeCare");
      } else {
        alert("Okänt journalsystem valt.");
        return;
      }

      // PMO behöver en lista som är 1-till-1 med mallen
      let rubrikerToSend = gptRubriker;

      if (selectedJournalSystem === "PMO") {
        const fullTemplateSecs = [...(selectedTemplate?.sections || [])]
          .sort((a, b) => a.order - b.order);

        rubrikerToSend = fullTemplateSecs.map((tmplSec) => {
          const hit = sections.find(
            (sec) =>
              sec.title.trim().toLowerCase() ===
              tmplSec.section_title.trim().toLowerCase()
          );
          return {
            title: tmplSec.section_title,
            content: hit ? hit.content.trim() : " ", // blanksteg fyller luckan
          };
        });
      }

      // Skicka till det lokala verktyget
      const response = await fetch(ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rubriker: rubrikerToSend }),
      });

      if (!response.ok) {
        throw new Error(`Kunde inte skicka data. Status: ${response.statusText}`);
      }

      // TakeCare hanterar egen feedback via desktop-verktyget
      if (selectedJournalSystem !== "TakeCare") {
        alert(`Journalen har skickats till ${selectedJournalSystem}!`);
      }
    } catch (err) {
      console.error("Fel i handleTransferClick:", err);
      // TakeCare hanterar egen feedback via desktop-verktyget
      if (selectedJournalSystem !== "TakeCare") {
        alert("Något gick fel vid överföring av journalen.");
      }
    }
  };

  // Överför dokument till TakeCare (bara för Rehabiliteringsplan)
  const handleTransferDocument = async (doc) => {
    try {
      // Öppna TakeCare-fönstret
      window.aionDesktop?.startTransfer?.("TakeCare");

      // Bygg rubrik-arrayen
      const rubriker = doc.sections.map((s) => ({
        title: s.title,
        content: (s.content || "").trim() || " ",
      }));

      // Skicka till lokala verktyget
      const resp = await fetch("http://127.0.0.1:5010/send", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rubriker }),
      });

      if (!resp.ok) throw new Error(await resp.text());
      alert("Dokumentet har skickats till TakeCare!");
    } catch (e) {
      console.error("Fel vid dokument-transfer:", e);
      alert("Kunde inte föra över dokumentet till TakeCare.");
    }
  };

  return {
    handleTransferClick,
    handleTransferDocument,
    selectedJournalSystem,
    // Signeringsdialog state och callbacks
    showSignPopup,
    hasCertificate,
    signError,
    isSigningLoading,
    handleSignPdf,
    handleSkipSign,
    handleCancelSign
  };
};